import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EuroIcon from '@mui/icons-material/Euro';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActivityService from '../../services/ActivityService';
import { fmtDate, fmtDateTime, fmtMoney } from '../../utils/format';

const ICON = {
    CREATED: [AddCircleOutlineIcon, '#10B981'], UPDATED: [EditOutlinedIcon, '#427898'], DELETED: [DeleteOutlineIcon, '#EF4444'],
    STATUS_CHANGED: [SwapHorizIcon, '#F59E0B'], SCHEDULED: [EventOutlinedIcon, '#427898'], ASSIGNED: [GroupOutlinedIcon, '#8B5CF6'],
    LOCATION_CHANGED: [PlaceOutlinedIcon, '#EF4444'], PRICE_CHANGED: [EuroIcon, '#B45309'], PAYMENT: [EuroIcon, '#059669'],
    STEP_DONE: [TaskAltIcon, '#10B981'], STEP_UNDONE: [RadioButtonUncheckedIcon, '#94A3B8'],
    FILE_ADDED: [AttachFileIcon, '#427898'], FILE_DELETED: [AttachFileIcon, '#EF4444'],
    VISIT_ADDED: [DirectionsWalkIcon, '#0EA5E9'], VISIT_UPDATED: [DirectionsWalkIcon, '#0EA5E9'], VISIT_DELETED: [DirectionsWalkIcon, '#EF4444'],
};

/** A logged value in the user's language (statuses, dates, money, yes/no); empty values show as a dash. */
function useValue() {
    const { t } = useTranslation();
    return (field, v) => {
        if (v == null || v === '') return '—';
        switch (field) {
            case 'status': return t(`status.${v}`);
            case 'scheduledDate': case 'processedOn': case 'visitDate': return fmtDate(v);
            case 'price': return fmtMoney(v);
            case 'includeParcelling': return v === 'true' ? t('activity.yes') : t('activity.no');
            case 'type': return t(`clients.type.${v}`);
            default: return v;
        }
    };
}

/** The extra facts of an entry (file name, checklist step, visit date, quote, payment, note excerpt, pro-forma number) as one line. */
function useExtra() {
    const { t } = useTranslation();
    return (a) => {
        const e = a.extra || {};
        const bits = [];
        if (e.step) bits.push(t(`steps.${e.step}`));
        if (e.name) bits.push(e.name);
        if (e.date && a.action !== 'PAYMENT') bits.push(fmtDate(e.date));
        if (e.quote) bits.push(t('activity.fromQuote', { number: e.quote }));
        if (e.number) bits.push(e.number);
        if (e.note) bits.push(`„${e.note}“`);
        if (a.action === 'PAYMENT') {
            const vars = { amount: fmtMoney(e.amount), date: fmtDate(e.date) };
            if (e.event) bits.push(t('activity.paymentLine', vars) + (e.note ? ` (${e.note})` : ''));
            // Entries from before payments could be split: one "paid" switch per job.
            else bits.push(e.paid === 'true' ? t('activity.paidOn', vars) : t('activity.markedUnpaid'));
        }
        return bits.join(' · ');
    };
}

export function ActivityItem({ a, showSubject }) {
    const { t } = useTranslation();
    const value = useValue();
    const extra = useExtra()(a);
    const [Icon, color] = ICON[a.action] || ICON.UPDATED;
    const kind = a.entityType === 'CLIENT' ? 'client' : 'job';
    const subjectLink = a.action === 'DELETED' ? null : (a.entityType === 'CLIENT' ? `/clients/${a.entityId}` : `/jobs/${a.entityId}`);
    return (
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: '1px solid #F5F7FA', '&:last-of-type': { borderBottom: 0 } }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                <Icon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, color: '#1E293B', lineHeight: 1.45 }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>{a.userName}</Box>{' '}
                    {t(`activity.actions.${a.action}`, { context: a.action === 'PAYMENT' && a.extra?.event === 'deleted' ? 'deleted' : kind })}
                    {showSubject && (
                        <>
                            {' '}
                            {subjectLink
                                ? <Link component={RouterLink} to={subjectLink} underline="hover" sx={{ fontWeight: 600, color: '#427898' }}>{a.subject}</Link>
                                : <Box component="span" sx={{ fontWeight: 600 }}>{a.subject}</Box>}
                        </>
                    )}
                </Typography>
                {extra && <Typography sx={{ fontSize: 12.5, color: '#475569', mt: 0.25, wordBreak: 'break-word' }}>{extra}</Typography>}
                {a.changes.map((c, i) => (
                    <Typography key={i} sx={{ fontSize: 12.5, color: '#475569', mt: 0.25, wordBreak: 'break-word' }}>
                        <Box component="span" sx={{ color: '#94A3B8' }}>{t(`activity.fields.${c.field}`)}:</Box>{' '}
                        <Box component="span" sx={{ textDecoration: c.from ? 'line-through' : 'none', color: '#94A3B8' }}>{value(c.field, c.from)}</Box>
                        {' → '}
                        <Box component="span" sx={{ fontWeight: 600 }}>{value(c.field, c.to)}</Box>
                    </Typography>
                ))}
                <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mt: 0.25 }}>{fmtDateTime(a.at)}</Typography>
            </Box>
        </Box>
    );
}

/**
 * History of a job or client (pass `jobId` or `clientId`), or the firm's log filtered by `userId`/`from`/`to`
 * (admins), newest first, with "show more". `reloadKey` refreshes it after a change on the page.
 */
export default function ActivityList({ jobId, clientId, userId, from, to, reloadKey, showSubject = false, pageSize = 15 }) {
    const { t } = useTranslation();
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);

    const load = useCallback((p) => {
        ActivityService.list({ jobId, clientId, userId: userId || undefined, from: from || undefined, to: to || undefined, page: p, size: pageSize })
            .then(r => { setItems(prev => (p === 0 ? r.items : [...(prev || []), ...r.items])); setTotal(r.total); setPage(p); })
            .catch(() => setItems(prev => prev || []));
    }, [jobId, clientId, userId, from, to, pageSize]);

    useEffect(() => { load(0); }, [load, reloadKey]);

    if (items === null) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>;
    if (!items.length) return <Typography sx={{ px: 2.5, py: 3, fontSize: 13.5, color: '#94A3B8', textAlign: 'center' }}>{t('activity.empty')}</Typography>;
    return (
        <Box sx={{ px: 2.5, py: 0.5 }}>
            {items.map(a => <ActivityItem key={a.id} a={a} showSubject={showSubject} />)}
            {items.length < total && (
                <Box sx={{ textAlign: 'center', py: 1.25 }}>
                    <Button size="small" onClick={() => load(page + 1)}>{t('activity.showMore', { count: total - items.length })}</Button>
                </Box>
            )}
        </Box>
    );
}
